import type { Components } from '@/api/generated/api';
import { useDisplayName } from '@/composables/useDisplayName';
import type { ISelectedCompetence, TStudentLike } from '@/interfaces';
import { toCompetenceSelectionRecord } from '@/utils/competence.domain.utils';
import { getEligibleStudentsForSubject } from '@/utils/students.utils';
import { isStudentEntryFullyRated } from 'common/utils/performance.assessment.entry.utils';
import { isNotNullish } from 'common/utils/array.utils';
import { nonEmpty } from 'common/utils/string.utils';
import { computed, type ComputedRef, type Ref } from 'vue';
import type { IPerformanceAssessmentEntryStudentView } from './performanceAssessmentEntryDetailDialog.types';

interface IPerformanceAssessmentEntryDetailViewModelComposable {
  allAvailableCompetenceDomains: ComputedRef<Components.Schemas.CompetenceDomainDto[]>;
  competenceSelectionById: ComputedRef<Partial<Record<number, ISelectedCompetence>>>;
  eligibleStudents: ComputedRef<Components.Schemas.StudentWithSubjectAccessDto[]>;
  openStudentCount: ComputedRef<number>;
  performanceAssessment: ComputedRef<Components.Schemas.BasePerformanceAssessmentDto | null>;
  schoolSubject: ComputedRef<Components.Schemas.BaseSchoolSubjectDto | null>;
  selectableStudents: ComputedRef<TStudentLike[]>;
  selectedCompetences: ComputedRef<ISelectedCompetence[]>;
  studentViews: ComputedRef<IPerformanceAssessmentEntryStudentView[]>;
}

export const usePerformanceAssessmentEntryDetailViewModel = (params: {
  allCompetenceDomains: Ref<Components.Schemas.CompetenceDomainDto[]>;
  allStudents: Ref<Components.Schemas.StudentWithSubjectAccessDto[]>;
  entry: Ref<Components.Schemas.PerformanceAssessmentEntryDto | null>;
}): IPerformanceAssessmentEntryDetailViewModelComposable => {
  const { getNameSortValue } = useDisplayName();

  const performanceAssessment = computed(() => params.entry.value?.performanceAssessment ?? null);
  const schoolSubject = computed(() => params.entry.value?.schoolSubject ?? null);
  const allAvailableCompetenceDomains = computed(() => params.allCompetenceDomains.value);
  const competenceSelectionById = computed(() => toCompetenceSelectionRecord(allAvailableCompetenceDomains.value));

  const eligibleStudents = computed(() =>
    getEligibleStudentsForSubject(params.allStudents.value, schoolSubject.value?.id ?? null),
  );

  const studentViews = computed<IPerformanceAssessmentEntryStudentView[]>(() => {
    const entry = params.entry.value;
    if (!entry) return [];
    return entry.studentEntries
      .map((studentEntry) => {
        const observations = entry.competenceObservations.filter(
          (observation) => observation.studentId === studentEntry.studentId,
        );
        const overallRating =
          entry.performanceAssessment?.performanceVisual?.performanceLevelsV2.find(
            (level) => level.id === studentEntry.performanceLevelV2Id,
          ) ?? null;
        return {
          student: studentEntry.student,
          observations,
          overallRating,
          gradeValue: studentEntry.gradeValue,
          competenceRatings: observations.flatMap((observation) =>
            observation.performanceLevelV2 ? [observation.performanceLevelV2] : [],
          ),
          fullyRated: isStudentEntryFullyRated(studentEntry, entry.competenceObservations, entry.performanceAssessment),
          hasNote: nonEmpty(studentEntry.description),
        };
      })
      .sort((a, b) => getNameSortValue(a.student).localeCompare(getNameSortValue(b.student)));
  });

  // Students of the entry that are no longer eligible stay selectable so they can be removed here; once removed
  // they are gone from the roster, so they cannot be selected again.
  const selectableStudents = computed<TStudentLike[]>(() => {
    const eligibleIds = new Set(eligibleStudents.value.map(({ id }) => id));
    const documentedStudents = studentViews.value
      .map(({ student }) => student)
      .filter(({ id }) => !eligibleIds.has(id));
    return [...eligibleStudents.value, ...documentedStudents];
  });

  const selectedCompetences = computed<ISelectedCompetence[]>(() => {
    const competenceIds = new Set(
      params.entry.value?.competenceObservations.map((observation) => observation.competenceId) ?? [],
    );
    return [...competenceIds].map((competenceId) => competenceSelectionById.value[competenceId]).filter(isNotNullish);
  });

  const openStudentCount = computed(() => studentViews.value.filter((student) => !student.fullyRated).length);

  return {
    allAvailableCompetenceDomains,
    competenceSelectionById,
    eligibleStudents,
    openStudentCount,
    performanceAssessment,
    schoolSubject,
    selectableStudents,
    selectedCompetences,
    studentViews,
  };
};
